import type { Module } from "vuex";

import { PLATFORM } from "hoxton-shared/enums/exports";

import { JobStatus } from "@/enums/jobs";

import type { RootState } from "@/store/types";

const storeNamespace = "jobs";

export const JobsMutation = {
    DeleteNotificationIdByActiveJobId: "deleteNotificationIdByActiveJobId",
    AddCompletedJob: "addCompletedJob",
    SetNotificationIdByActiveJobId: "setNotificationIdByActiveJobId"
};

export const JobsAction = {
    DeleteNotificationIdByActiveJobId: `${storeNamespace}/${JobsMutation.DeleteNotificationIdByActiveJobId}`,
    SetNotificationIdByActiveJobId: `${storeNamespace}/${JobsMutation.SetNotificationIdByActiveJobId}`
};

type Job =
    | "UnpublishXmlFeed"
    | "PublishXmlFeed"
    | "AddMetadataValue"
    | "RemoveMetadataValue"
    | "VideoDownloader"
    | "PsdDownloader"
    | "ScreenshotDownloader"
    | "PackDownloader"
    | "PUBFT_DOWNLOAD_EXCEL"
    | "PUBFT_DOWNLOAD_ASSETS"
    | "PlainlyBatchRender"
    | "ADOBE_INDESIGN_DOWNLOAD";

type JobStatusType = (typeof JobStatus)[keyof typeof JobStatus];

export type JobNotification = {
    _id: string;
    name: Job;
    aborted: null;
    completed: string;
    completedJobs?: number;
    failed: unknown;
    message: string;
    progress: null;
    status: JobStatusType;
    totalJobs?: number;
    campaignDetails: {
        _id: string;
        clientId: string;
        name: string;
    };
    clientDetails: {
        _id: string;
        id: string;
        name: string;
    };

    payload: {
        archiveName: string;
        campaignId: string;
        computedDeliverablesIdentifiers: {
            editableGroupValueIds: string[];
            masterTemplateId: string;
            language: string;
        }[];
        defaultFonts: unknown;
        deviceScaleFactor: number;
        contentType?: string;
        format?: string;
        inProgressProcessor: string;
        libraryFilters: null;
        preProcessor: string;
        progress?: Record<
            string,
            {
                label: string;
                message: string;
                percent: number;
            }
        >;
        stack: string;
        startedAt: string;
        totalCount: number;
        type?: string;
        clientId?: string;
        clientName?: string;
        zipUrl?: string;
        failedTasks?: {
            label: string;
            hash: string;
        }[];
        platform?: (typeof PLATFORM)[keyof typeof PLATFORM];
        publishingAdsCount?: number;
        processingAdsCount?: number;
        tikTokData?: unknown;
        facebookData?: unknown;
        xmlFeedUrls?: string[];
        isRendering?: boolean;
    };

    __typename: "Job";
};

export type State = {
    completedJobs: JobNotification[];
    notificationIdsByActiveJobId: Record<string, number>;
};

const getDefaultState = (): State => ({
    completedJobs: [],
    notificationIdsByActiveJobId: {}
});

const jobsModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        deleteNotificationIdByActiveJobId(state, jobId: string) {
            delete state.notificationIdsByActiveJobId[jobId];
        },

        setNotificationIdByActiveJobId(state, { notificationId, jobId }: { notificationId: number; jobId: string }) {
            state.notificationIdsByActiveJobId[jobId] = notificationId;
        },

        addCompletedJob(state, job: State["completedJobs"][number]) {
            state.completedJobs = [...state.completedJobs, job];
        }
    },

    actions: {
        deleteNotificationIdByActiveJobId({ commit }, job: State["completedJobs"][number]) {
            if (job.status === JobStatus.COMPLETE) {
                commit(JobsMutation.AddCompletedJob, job);
            }
            commit(JobsMutation.DeleteNotificationIdByActiveJobId, job._id);
        },

        setNotificationIdByActiveJobId(
            { commit },
            { notificationId, jobId }: { notificationId: number; jobId: string }
        ) {
            commit(JobsMutation.SetNotificationIdByActiveJobId, { notificationId, jobId });
        }
    }
};

export default jobsModule;
